import { motion } from "framer-motion";
import { Sparkles, Shield, DollarSign, Users, Clock, Award } from "lucide-react";

const reasons = [
  {
    icon: Sparkles,
    title: "Detail-Driven Lawn Care",
    desc: "Every visit includes a clean, even cut, crisp edging, blowing, and weeding — the details that make a yard look professionally kept.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Fully Insured Crews",
    desc: "Our team is fully insured and arrives with the right equipment. Your property is protected on every visit.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Honest, Transparent Pricing",
    desc: "Free written estimates with no hidden fees. Returning customers and referrals save 10% on their next service.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: "Available 24/7",
    desc: "Reach out any time — we're available around the clock to answer questions and get your service scheduled.",
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Johnson County Local",
    desc: "Headquartered in Olathe, KS and proudly serving Overland Park, Leawood, Shawnee, Lenexa, Prairie Village, and the surrounding area.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Owner-Operated",
    desc: "Owner Sergio Zaldana personally stands behind every job. Friendly, reliable service you can count on.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why B&S Services KC
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons homeowners and businesses across Johnson County trust us with their lawns and landscaping.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
